import type { Project } from '@hangar/shared';
import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useEditProjectForm } from './useEditProjectForm';

interface Props {
  project: Project | null;
  onOpenChange: (open: boolean) => void;
}

type EditState = ReturnType<typeof useEditProjectForm>;

/** Rename a project or change its description (`PATCH /projects/{id}`, If-Match). The slug is fixed. */
export function EditProjectDialog({ project, onOpenChange }: Props) {
  return (
    <Dialog open={Boolean(project)} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-project-dialog" aria-describedby="edit-project-desc">
        {project ? <EditProjectForm key={project.id} project={project} onDone={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function NameField({ f }: { f: EditState }) {
  const error = f.errorFor('name');
  return (
    <FormField id="edit-project-name" label="Project name" required error={error}>
      <Input data-testid="edit-project-name" value={f.values.name} maxLength={120} autoComplete="off" onChange={(e) => f.set('name', e.target.value)} {...fieldA11y('edit-project-name', error)} />
    </FormField>
  );
}

/** The slug is fixed: the CLI and the API use it. */
function SlugField({ slug }: { slug: string }) {
  return (
    <FormField id="edit-project-slug" label="Project slug" hint="Fixed: the CLI and the API use it" trailing={<Icon name="lock" size={14} className="text-subtle" />}>
      <div className="relative">
        <Input id="edit-project-slug" data-testid="edit-project-slug" value={slug} readOnly aria-readonly className="cursor-not-allowed bg-secondary pr-9 font-mono text-muted-foreground sm:text-[13px]" aria-describedby="edit-project-slug-hint" />
        <Icon name="lock" size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-subtle" />
      </div>
    </FormField>
  );
}

function DescriptionField({ f }: { f: EditState }) {
  const error = f.errorFor('description');
  return (
    <FormField id="edit-project-description" label="Description" error={error}>
      <Textarea data-testid="edit-project-description" value={f.values.description} maxLength={2000} placeholder="What ships from this project" onChange={(e) => f.set('description', e.target.value)} {...fieldA11y('edit-project-description', error)} />
    </FormField>
  );
}

function EditProjectFooter({ onDone, pending }: { onDone: () => void; pending: boolean }) {
  return (
    <DialogFooter>
      <Button variant="ghost" onClick={onDone}>
        Cancel
      </Button>
      <Button type="submit" data-testid="edit-project-submit" loading={pending}>
        Save
      </Button>
    </DialogFooter>
  );
}

function EditProjectForm({ project, onDone }: { project: Project; onDone: () => void }) {
  const f = useEditProjectForm(project, onDone);
  return (
    <form data-testid="edit-project-form" onSubmit={(e) => void f.onSubmit(e)} noValidate className="flex min-h-0 flex-col" aria-label={`Edit ${project.name}`}>
      <DialogHeader>
        <DialogTitle>Edit project</DialogTitle>
        <DialogDescription id="edit-project-desc">Basic project identity. Environments, members and limits arrive with Project settings.</DialogDescription>
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <NameField f={f} />
          <SlugField slug={project.slug} />
        </div>
        <DescriptionField f={f} />
        <ProblemAlert error={f.problem} testId="edit-project-problem" />
      </DialogBody>
      <EditProjectFooter onDone={onDone} pending={f.pending} />
    </form>
  );
}
